import { Injectable } from '@angular/core';
import { NotificationService } from './notifications.service';
import { TranslateService } from '@ngx-translate/core';
import { finalize, Observable, Subject, Subscription, takeUntil } from 'rxjs';
import { ERROR_MESSAGES_STRING } from '../enum/constants';

@Injectable({ providedIn: 'root' })
export class RequestHelper {
  private cancelMap = new Map<string, Subject<void>>();

  constructor(
    private readonly translateService: TranslateService,
    private readonly notificationService: NotificationService,
  ) {
  }

  handle<T>(
    request$: Observable<T>,
    onSuccess: (res: T) => void,
    options: {
      key?: string;
      destroy$: Subject<void>;
      onError?: (e: any) => void;
      onFinalize?: () => void;
      loaderRef?: Record<string, boolean>;
      loaderKey?: string;
      defaultErrorMessage?: string;
      ignoreErrorCodes?: string[];
    },
  ): Subscription {
    const {
      key,
      destroy$,
      onError,
      onFinalize,
      loaderRef,
      loaderKey,
      defaultErrorMessage,
      ignoreErrorCodes = [],
    } = options;

    if (key && this.cancelMap.has(key)) {
      this.cancelMap.get(key)!.next();
    }

    const cancel$ = new Subject<void>();
    if (key) {
      this.cancelMap.set(key, cancel$);
    }

    if (loaderRef && loaderKey) loaderRef[loaderKey] = true;

    return request$
      .pipe(
        takeUntil(destroy$),
        takeUntil(cancel$),
        finalize(() => {
          if (loaderRef && loaderKey) loaderRef[loaderKey] = false;
          onFinalize?.();
          if (key) {
            this.cancelMap.delete(key);
          }
        }),
      )
      .subscribe({
        next: (res) => {
          onSuccess?.(res);
        },
        error: (e) => {
          const errorCode = e.error?.errors?.[0]?.code;
          const errorMessage = ERROR_MESSAGES_STRING[errorCode] || defaultErrorMessage;

          if (errorMessage && !ignoreErrorCodes.includes(errorCode)) {
            this.notificationService.showError(
              this.translateService.instant('notifications_texts.error.error'),
              this.translateService.instant(errorMessage),
            );
          }

          onError?.(e);
        },
      });
  }
}
